import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Anchor, ArrowLeft, Check, Loader2, ShieldCheck, Sparkles, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/use-toast'
import { ParsedRecipePreview } from '@/components/ai/parsed-recipe-preview'
import { aiRecipeParser, type AIRecipeIdea } from '@/services/ai-recipe-parser'
import {
  buildMenuCandidates,
  buildPoolCandidates,
  selectAnchorRecipes,
  toAnchorSummaries,
  toCandidateSummaries,
} from '@/services/menu-service'
import { useHouseholdStore } from '@/stores/household-store'
import { useRecipeStore } from '@/stores/recipe-store'
import { useSettingsStore } from '@/stores/settings-store'
import type { Recipe } from '@/types/recipe'

const BATCH_SIZE = 3

interface DraftDish {
  idea: AIRecipeIdea
  recipe?: Partial<Recipe>
  isExpanding: boolean
  isPublishing: boolean
  isRefining: boolean
}

export default function MenuPoolPage() {
  const { recipes, loadRecipes, createRecipe, updateRecipe } = useRecipeStore()
  const { currentHousehold, viewMode } = useHouseholdStore()
  const { aiEnabled } = useSettingsStore()
  const { toast } = useToast()

  const [preferences, setPreferences] = useState('')
  const [drafts, setDrafts] = useState<DraftDish[]>([])
  const [isGenerating, setIsGenerating] = useState(false)
  const [platformAIConfigured, setPlatformAIConfigured] = useState<boolean | null>(
    aiRecipeParser.peekStatus()
  )

  const isAIAvailable = aiEnabled && platformAIConfigured !== false

  useEffect(() => {
    void aiRecipeParser.getStatus().then(setPlatformAIConfigured)
  }, [])

  useEffect(() => {
    void loadRecipes()
  }, [loadRecipes])

  const pool = useMemo(() => buildPoolCandidates(recipes), [recipes])
  const anchors = useMemo(() => selectAnchorRecipes(recipes), [recipes])

  const updateDraft = (ideaId: string, updater: (draft: DraftDish) => DraftDish) =>
    setDrafts((current) =>
      current.map((draft) => (draft.idea.id === ideaId ? updater(draft) : draft))
    )

  const handleGenerate = async () => {
    const candidates = buildMenuCandidates(recipes)
    if (!candidates.length) {
      toast({
        title: 'Nog geen recepten om op te bouwen',
        description: 'Voeg eerst recepten toe; nieuwe gerechten worden daarop gebaseerd.',
        variant: 'destructive',
      })
      return
    }

    setIsGenerating(true)
    const result = await aiRecipeParser.curateWeekMenu(toCandidateSummaries(candidates), {
      anchors: toAnchorSummaries(anchors),
      count: 0,
      ideaCount: BATCH_SIZE,
      preferences: preferences.trim() || undefined,
    })
    setIsGenerating(false)

    if (!result.success || !result.ideas?.length) {
      toast({
        title: 'Genereren lukte niet',
        description: result.error || 'Er kwamen geen bruikbare gerechten terug.',
        variant: 'destructive',
      })
      return
    }

    setDrafts(
      result.ideas.map((idea) => ({
        idea,
        isExpanding: false,
        isPublishing: false,
        isRefining: false,
      }))
    )
  }

  const handleExpand = async (draft: DraftDish) => {
    updateDraft(draft.idea.id, (current) => ({ ...current, isExpanding: true }))
    const result = await aiRecipeParser.expandRecipeIdea(
      {
        title: draft.idea.title,
        description: draft.idea.description,
        why_it_fits: draft.idea.why_it_fits,
        anchor_id: draft.idea.anchor_id,
        anchor_title: draft.idea.anchor_title,
        prep_time: draft.idea.prep_time,
        cook_time: draft.idea.cook_time,
        servings: draft.idea.servings,
        difficulty: draft.idea.difficulty,
        meal_type: draft.idea.meal_type,
        tags: draft.idea.tags,
      },
      preferences.trim() || 'Een gerecht voor de weekmenu-pool'
    )
    updateDraft(draft.idea.id, (current) => ({ ...current, isExpanding: false }))

    if (!result.success || !result.recipe) {
      toast({
        title: 'Uitwerken lukte niet',
        description: result.error || 'Dit gerecht kon niet worden uitgewerkt.',
        variant: 'destructive',
      })
      return
    }

    updateDraft(draft.idea.id, (current) => ({ ...current, recipe: result.recipe }))
  }

  const handleRefine = async (draft: DraftDish, instruction: string) => {
    if (!draft.recipe) {
      return
    }

    updateDraft(draft.idea.id, (current) => ({ ...current, isRefining: true }))
    const result = await aiRecipeParser.refineRecipe(draft.recipe, instruction)
    updateDraft(draft.idea.id, (current) => ({ ...current, isRefining: false }))

    if (!result.success || !result.recipe) {
      toast({
        title: 'Aanpassen lukte niet',
        description: result.error || 'Deze wijziging kon niet worden verwerkt.',
        variant: 'destructive',
      })
      return
    }

    updateDraft(draft.idea.id, (current) => ({
      ...current,
      recipe: { ...current.recipe, ...result.recipe },
    }))
  }

  const handlePublish = async (draft: DraftDish) => {
    const recipe = draft.recipe
    if (!recipe?.title || !recipe.ingredients?.length || !recipe.instructions?.length) {
      return
    }

    updateDraft(draft.idea.id, (current) => ({ ...current, isPublishing: true }))
    const created = await createRecipe(
      {
        title: recipe.title,
        description: recipe.description || '',
        ingredients: recipe.ingredients,
        instructions: recipe.instructions,
        prep_time: recipe.prep_time || null,
        cook_time: recipe.cook_time || null,
        servings: recipe.servings || null,
        difficulty: recipe.difficulty || '',
        meal_type: recipe.meal_type || 'diner',
        tags: recipe.tags || [],
        equipment: recipe.equipment || [],
        image_url: recipe.image_url || '',
        rating: null,
        notes: recipe.notes || '',
        is_public: true,
        is_curated: true,
      },
      viewMode === 'household' ? currentHousehold?.id : undefined
    )
    updateDraft(draft.idea.id, (current) => ({ ...current, isPublishing: false }))

    if (!created) {
      toast({
        title: 'Publiceren lukte niet',
        description: 'Het gerecht kon niet aan de pool worden toegevoegd.',
        variant: 'destructive',
      })
      return
    }

    setDrafts((current) => current.filter((item) => item.idea.id !== draft.idea.id))
    toast({
      title: 'Toegevoegd aan de pool',
      description: `"${created.title}" staat nu klaar voor het weekmenu.`,
    })
  }

  const handleRemoveFromPool = async (recipe: Recipe) => {
    const updated = await updateRecipe(recipe.id, { is_curated: false })
    if (!updated) {
      toast({
        title: 'Verwijderen lukte niet',
        description: 'Het gerecht kon niet uit de pool worden gehaald.',
        variant: 'destructive',
      })
      return
    }

    toast({
      title: 'Uit de pool gehaald',
      description: `"${recipe.title}" staat niet meer in het weekmenu.`,
    })
  }

  return (
    <div className="container py-8">
      <Link
        to="/weekmenu"
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:underline"
      >
        <ArrowLeft className="h-4 w-4" />
        Terug naar weekmenu
      </Link>

      <div className="mb-8">
        <p className="text-sm font-medium uppercase tracking-wide text-orange-600">Gerechtenpool</p>
        <h1 className="mt-1 text-3xl font-bold text-gray-900 dark:text-slate-50">
          Gecontroleerde gerechten
        </h1>
        <p className="mt-2 max-w-2xl text-gray-600 dark:text-slate-300">
          Gerechten in de pool zijn door jou nagekeken en gaan zonder extra AI-werk het weekmenu in.
          Hoe voller de pool, hoe minder er tijdens het browsen gegenereerd wordt.
        </p>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Nieuwe gerechten voorstellen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isAIAvailable ? (
            <p className="text-sm text-gray-600 dark:text-slate-300">
              Slimme hulp staat uit. Zet die aan in Instellingen om nieuwe gerechten te laten
              voorstellen — bestaande poolgerechten blijven gewoon werken.
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-slate-300">
                Voorstellen worden gebaseerd op{' '}
                {anchors.length > 0 ? (
                  <span className="font-medium">
                    {anchors.length} hoog gewaardeerd{anchors.length === 1 ? ' recept' : 'e recepten'}
                  </span>
                ) : (
                  'je bestaande recepten'
                )}
                . Werk ze uit, controleer ze en publiceer wat goed is.
              </p>

              <div className="flex flex-col gap-3 sm:flex-row">
                <Input
                  value={preferences}
                  onChange={(event) => setPreferences(event.target.value)}
                  placeholder="Bijv. meer vis, of juist vegetarisch..."
                  className="sm:max-w-md"
                />
                <Button onClick={() => void handleGenerate()} disabled={isGenerating}>
                  {isGenerating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="mr-2 h-4 w-4" />
                  )}
                  Stel {BATCH_SIZE} gerechten voor
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {drafts.length > 0 && (
        <div className="mb-10 space-y-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-50">
            Te controleren ({drafts.length})
          </h2>

          {drafts.map((draft) => (
            <Card key={draft.idea.id}>
              <CardContent className="space-y-4 p-5">
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-50">
                    {draft.idea.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-slate-300">
                    {draft.idea.description}
                  </p>
                  {draft.idea.anchor_title && (
                    <p className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-slate-400">
                      <Anchor className="mt-0.5 h-3 w-3 flex-shrink-0" />
                      <span>
                        Gebaseerd op <span className="font-medium">{draft.idea.anchor_title}</span>
                      </span>
                    </p>
                  )}
                  {draft.idea.why_it_fits && (
                    <p className="rounded-lg bg-orange-50 px-3 py-2 text-xs text-orange-900 dark:bg-orange-950/60 dark:text-orange-100">
                      {draft.idea.why_it_fits}
                    </p>
                  )}
                </div>

                {draft.recipe ? (
                  <ParsedRecipePreview
                    parsedRecipe={draft.recipe}
                    onSave={() => handlePublish(draft)}
                    onRefineRecipe={(instruction) => handleRefine(draft, instruction)}
                    isRefiningRecipe={draft.isRefining}
                    isSaving={draft.isPublishing}
                  />
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => void handleExpand(draft)}
                      disabled={draft.isExpanding}
                    >
                      {draft.isExpanding ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Werk uit &amp; controleer
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-500"
                      onClick={() =>
                        setDrafts((current) =>
                          current.filter((item) => item.idea.id !== draft.idea.id)
                        )
                      }
                    >
                      Overslaan
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="space-y-4">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-slate-50">
          <ShieldCheck className="h-5 w-5 text-green-600" />
          In de pool ({pool.length})
        </h2>

        {pool.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="mx-auto max-w-md text-sm text-gray-600 dark:text-slate-300">
                De pool is nog leeg. Zolang dat zo is, vult het weekmenu zichzelf aan met verse
                AI-gerechten. Publiceer hier gerechten om dat over te nemen.
              </p>
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-2">
            {pool.map((recipe) => (
              <li
                key={recipe.id}
                className="flex items-center gap-3 rounded-lg border border-gray-200 p-3 dark:border-slate-700"
              >
                <Check className="h-4 w-4 flex-shrink-0 text-green-600" />
                <Link
                  to={`/recepten/${recipe.id}`}
                  className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 hover:text-orange-600 dark:text-slate-50"
                >
                  {recipe.title}
                </Link>
                <span className="hidden text-xs text-gray-500 sm:inline dark:text-slate-400">
                  {recipe.ingredients.length} ingrediënten
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 flex-shrink-0 p-0 text-gray-400 hover:text-red-600"
                  onClick={() => void handleRemoveFromPool(recipe)}
                  aria-label={`Haal ${recipe.title} uit de pool`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
